import React, {useRef, useState, useEffect, memo, useCallback} from 'react';
import paper from 'paper';
import { useTranslation } from "@/lib/i18n";
import {
  MousePointer,
  Square,
  Circle,
  PenTool,
  Type,
  Merge,
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Underline,
  Plus,
  Group,
  Ungroup,
  Triangle,
  Hexagon,
  Octagon,
  Wand2,
  Upload,
  Star,
  Minus,
  MoveRight,
  Diamond,
  Shapes,
  FlipHorizontal,
  FlipVertical,
  RotateCw,
  Scaling,
  Maximize,
  Move,
  Radius,
} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {importSvgFile} from './use-svg-importer';


const RoundedTriangleIcon = ({className = 'w-4 h-4'}) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
  </svg>
);

const EllipseIcon = ({className = 'w-4 h-4'}) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <ellipse cx="12" cy="12" rx="9" ry="5"/>
  </svg>
);

const getFileNameWithoutExtension = (filename) => {
  return filename
    .replace(/\.[^/.]+$/, '')
    .replace(/\s+/g, '_');
};

const NumberInput = memo(({ label, value, onChange, theme, step = 0.01, min, max, icon: Icon }) => {
  const [inputValue, setInputValue] = useState(value);
  const timerRef = useRef(null);
  const accelerationRef = useRef(1);
  const valueRef = useRef(value);

  const stopAdjusting = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;

      onChange(valueRef.current, true);
    }
  }, [onChange]);

  useEffect(() => {
    setInputValue(value);
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      stopAdjusting();
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      stopAdjusting();
    };
  }, [stopAdjusting]);

  const handleInternalChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleBlur = () => {
    const val = parseFloat(parseFloat(inputValue).toFixed(2));
    if (!isNaN(val)) {
      onChange(val, true);
    } else {
      setInputValue(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  };

  const adjustValue = (delta, isFinal = false) => {
    const newVal = parseFloat((valueRef.current + delta).toFixed(2));
    valueRef.current = newVal;
    setInputValue(newVal);
    onChange(newVal, isFinal);
  };

  const startAdjusting = (delta) => {
    if (timerRef.current) return;

    adjustValue(delta, false);
    
    let counter = 0;
    accelerationRef.current = 1;
    
    const run = () => {
      counter++;
      if (counter > 5) {
        accelerationRef.current = Math.min(accelerationRef.current * 1.1, 50);
      }
      
      adjustValue(delta * accelerationRef.current, false);
      
      const nextDelay = Math.max(10, 100 / (1 + counter * 0.1));
      timerRef.current = setTimeout(run, nextDelay);
    };
    
    timerRef.current = setTimeout(run, 400);
  };

  return (
    <div className="flex flex-col gap-1 w-full">
      {label && (
        <label className={`text-[10px] uppercase font-bold flex items-center gap-1.5 ${theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'}`}>
          {Icon && <Icon className="w-3 h-3" />} {label}
        </label>
      )}
      <div className={`flex items-center border rounded overflow-hidden ${
        theme === 'dark' ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-slate-200'
      }`}>
        <button
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            startAdjusting(-step);
          }}
          onMouseUp={stopAdjusting}
          onMouseLeave={stopAdjusting}
          className={`px-2 h-8 hover:bg-opacity-20 transition-colors flex items-center justify-center ${
            theme === 'dark' ? 'hover:bg-zinc-400 text-zinc-400' : 'hover:bg-slate-200 text-slate-500'
          }`}
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <input
          type="number"
          value={inputValue}
          onChange={handleInternalChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          step={step}
          min={min}
          max={max}
          className={`w-full bg-transparent text-[11px] text-center focus:outline-none py-1 appearance-none custom-number-input ${
            theme === 'dark' ? 'text-zinc-200' : 'text-slate-800'
          }`}
          style={{ MozAppearance: 'textfield' }}
        />
        <button
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            startAdjusting(step);
          }}
          onMouseUp={stopAdjusting}
          onMouseLeave={stopAdjusting}
          className={`px-2 h-8 hover:bg-opacity-20 transition-colors flex items-center justify-center ${
            theme === 'dark' ? 'hover:bg-zinc-400 text-zinc-400' : 'hover:bg-slate-200 text-slate-500'
          }`}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
});

export default function Sidebar({
    activeTool,
    setActiveTool,
    selectedCount,
    onBooleanOperation,
    onGroup,
    onUngroup,
    onProjectChange,
    onProjectNameChange,
    updateTransformUI,
    theme = 'dark',
    viewMode,
}) {
  const { t } = useTranslation('editor');
  const [svgContent, setSvgContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedItems = paper.project?.selectedItems.filter(item => 
    item.name !== 'grid' && !item.data?.isTransformHandle
  ) || [];
  
  const isAnyGroupSelected = selectedItems.some(item => item instanceof paper.Group);
  
  const textItem = selectedItems.length === 1 && (selectedItems[0] instanceof paper.PointText) 
    ? selectedItems[0] 
    : null;

  const handleTextChange = (prop, value) => {
    if (textItem) {
      textItem[prop] = value;

      if (prop === 'content') {
        const cleanText = value.trim()
          .toLowerCase()
          .replace(/[^a-zа-я0-9ієїґ\s\n]/g, '')
          .replace(/[\s\n]+/g, '_');
        textItem.name = `text_${cleanText}`;
      }

      onProjectChange?.();
      updateTransformUI?.();
    }
  };

  const handleTransformChange = (prop, value, isFinal = true) => {
    if (selectedItems.length === 0) return;

    selectedItems.forEach(item => {
      if (prop === 'rotation') {
        const currentRotation = item.data?.rotation || 0;
        const delta = value - currentRotation;
        item.rotate(delta);
        item.data = { ...item.data, rotation: value };
      } else if (prop === 'blur') {
        item.shadowBlur = value;
        item.shadowColor = value > 0 ? new paper.Color(0, 0, 0, 0.5) : null;
        item.shadowOffset = value > 0 ? new paper.Point(0, 0) : null;
        item.data = { ...item.data, blur: value };
      } else if (prop === 'x') {
        item.bounds.x = value;
      } else if (prop === 'y') {
        item.bounds.y = value;
      } else if (prop === 'width') {
        item.bounds.width = value;
      } else if (prop === 'height') {
        item.bounds.height = value;
      } else if (prop === 'radius') {
        if (item.data?.points) {
          const { createRoundedPolygonPath } = require('../canvas/shape-geometry');
          
          const oldRotation = item.data?.rotation || 0;
          if (oldRotation) {
            item.rotate(-oldRotation);
          }

          const roundedPath = createRoundedPolygonPath(item.data.points, value);
          
          roundedPath.insertAbove(item);
          roundedPath.fillColor = item.fillColor;
          roundedPath.strokeColor = item.strokeColor;
          roundedPath.strokeWidth = item.strokeWidth;
          roundedPath.strokeScaling = item.strokeScaling;
          roundedPath.name = item.name;
          roundedPath.shadowBlur = item.shadowBlur;
          roundedPath.shadowColor = item.shadowColor;
          roundedPath.shadowOffset = item.shadowOffset;
          
          roundedPath.data = { ...item.data, radius: value };
          
          if (oldRotation) {
            roundedPath.rotate(oldRotation);
            roundedPath.data.rotation = oldRotation;
          }
          
          roundedPath.selected = true;
          const idx = selectedItems.indexOf(item);
          if (idx !== -1) selectedItems[idx] = roundedPath;
          item.remove();
        } else if (item.data?.type === 'rect' || (item instanceof paper.Path) && item.segments.length === 4) {
          const oldRotation = item.data?.rotation || 0;
          
          if (oldRotation) {
            item.rotate(-oldRotation);
          }
          
          const rect = new paper.Path.Rectangle({
            rectangle: item.bounds,
            radius: value,
            insert: false
          });
          
          rect.insertAbove(item);
          rect.fillColor = item.fillColor;
          rect.strokeColor = item.strokeColor;
          rect.strokeWidth = item.strokeWidth;
          rect.strokeScaling = item.strokeScaling;
          rect.name = item.name;
          rect.shadowBlur = item.shadowBlur;
          rect.shadowColor = item.shadowColor;
          rect.shadowOffset = item.shadowOffset;
          
          rect.data = { ...item.data, type: 'rect', radius: value };
          
          if (oldRotation) {
            rect.rotate(oldRotation);
            rect.data.rotation = oldRotation;
          }
          
          rect.selected = true;
          const idx = selectedItems.indexOf(item);
          if (idx !== -1) selectedItems[idx] = rect;
          item.remove();
        }
      }
    });

    window.dispatchEvent(new CustomEvent('paper-item-transform'));
    if (isFinal) {
      onProjectChange?.();
    }
    updateTransformUI?.();
  };

  const handleFlip = (axis) => {
    if (selectedItems.length === 0) return;
    
    selectedItems.forEach(item => {
      const center = item.bounds.center;
      if (axis === 'horizontal') {
        item.scale(-1, 1, center);
      } else {
        item.scale(1, -1, center);
      }
    });

    window.dispatchEvent(new CustomEvent('paper-item-transform'));
    onProjectChange?.();
    updateTransformUI?.();
  };

  const renderTooltipButton = ({
                                 children,
                                 label,
                                 hotkey,
                                 onClick,
                                 toolKey,
                                 disabled = false,
                                 className = '',
                               }) => {
    const isActive = activeTool === toolKey;

    return (
      <Tooltip key={label}>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            variant={isActive ? 'default' : 'ghost'}
            disabled={disabled}
            onClick={onClick}
            className={`w-7 h-7 relative transition-all ${
              isActive
                ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-md shadow-indigo-500/20 ring-1 ring-indigo-400'
                : theme === 'dark'
                  ? 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800'
                  : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200'
            } ${className}`}
          >
            {children}
            {isActive && (
              <span className={`absolute -right-0.5 -top-0.5 w-1.5 h-1.5 bg-emerald-400 rounded-full ring-1 ${
                theme === 'dark' ? 'ring-zinc-900' : 'ring-slate-50'
              }`}/>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right"
                        className={`flex items-center gap-2 border shadow-xl ${
                          theme === 'dark' 
                            ? 'bg-zinc-800 border-zinc-700 text-zinc-200' 
                            : 'bg-white border-slate-200 text-slate-800'
                        }`}>
          <span>{label}</span>
          {hotkey && (
            <kbd
              className={`px-1.5 py-0.5 text-[10px] font-mono font-semibold rounded border ${
                theme === 'dark'
                  ? 'bg-zinc-900 text-zinc-400 border-zinc-700'
                  : 'bg-slate-100 text-slate-500 border-slate-200'
              }`}>
              {hotkey}
            </kbd>
          )}
        </TooltipContent>
      </Tooltip>
    );
  };

  const drawingTools = [
    {key: 'select', label: t('sidebar.tool_select'), hotkey: 'V', icon: <MousePointer className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'pen', label: t('sidebar.tool_path'), hotkey: 'P', icon: <PenTool className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'text', label: t('sidebar.tool_text'), hotkey: 'T', icon: <Type className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'line', label: t('sidebar.tool_line'), hotkey: 'L', icon: <Minus className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'arrow', label: t('sidebar.tool_arrow'), icon: <MoveRight className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'rhombus', label: t('sidebar.tool_rhombus'), icon: <Diamond className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'rect', label: t('sidebar.tool_rect'), hotkey: 'R', icon: <Square className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'square', label: t('sidebar.tool_square'), hotkey: 'Shift+R', icon: <Square className="w-5 h-5 stroke-[2.5]"/>},
    {key: 'ellipse', label: t('sidebar.tool_ellipse'), hotkey: 'E', icon: <EllipseIcon className="w-5 h-5"/>},
    {key: 'circle', label: t('sidebar.tool_circle'), hotkey: 'C', icon: <Circle className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'triangle', label: t('sidebar.tool_triangle'), hotkey: 'Y', icon: <Triangle className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'rounded-triangle', label: t('sidebar.rounded_triangle'), icon: <RoundedTriangleIcon className="w-5 h-5"/>},
    {key: 'hexagon', label: t('sidebar.tool_hexagon'), icon: <Hexagon className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'octagon', label: t('sidebar.tool_octagon'), icon: <Octagon className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'star', label: t('sidebar.tool_star'), icon: <Star className="w-5 h-5" strokeWidth={1.5}/>},
    {key: 'polygon-n', label: t('sidebar.tool_polygon_n'), icon: <Shapes className="w-5 h-5" strokeWidth={1.5}/>},
  ];

  const fileInputRef = useRef(null);
  const svgInputRef = useRef(null);

  const cleanupPaperItem = (item) => {
    item.applyMatrix = true;

    if (item.children) {
      if (item.clipped) {
        item.clipped = false;
        if (item.children[0]) {
          item.children[0].remove();
        }
      }

      item.children.forEach(cleanupPaperItem);
    }
  };

  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('page', 1);

    try {
      const response = await fetch( process.env.REACT_APP_PDF_SERVER + 'api/convert', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не вдалося конвертувати файл');
      }

      const svgText = await response.text();
      const convertedSvgFile = new File([svgText], `${file.name.replace(/\.pdf$/i, '')}.svg`, {
        type: 'image/svg+xml',
      });

      const items = await importSvgFile(convertedSvgFile, {
        onProjectChange,
        updateTransformUI,
        viewMode,
        theme,
      });

      if (items && items.length > 0) {
        items.forEach(cleanupPaperItem);
      }

      if (onProjectNameChange) {
        onProjectNameChange(getFileNameWithoutExtension(file.name));
      }

    } catch (err) {
      console.error('Помилка імпорту:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const onUploadSvg = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    try {
      const item = await importSvgFile(file, {
        onProjectChange,
        updateTransformUI,
        viewMode,
        theme,
      });

      if (onProjectNameChange) {
        onProjectNameChange(getFileNameWithoutExtension(file.name));
      }
    } catch (error) {
      console.error('Помилка імпорту:', error);
      alert(error.message || 'Помилка завантаження SVG-файлу');
    } finally {
      e.target.value = '';
    }
  };

  const allowedRadiusTypes = ['rect', 'square', 'triangle', 'rounded-triangle', 'hexagon', 'octagon', 'star'];
  const showRadius = selectedCount === 1 && allowedRadiusTypes.includes(selectedItems[0]?.data?.type);

  return (
    <aside
      className={`w-56 border rounded-lg p-2 flex flex-col justify-between items-center select-none gap-2.5 shadow-2xl transition-colors duration-300 ${
        theme === 'dark' ? 'bg-zinc-900 border-zinc-800 text-zinc-200' : 'bg-white border-slate-200 text-slate-800'
      }`}>
      <style>{`
        .custom-number-input::-webkit-outer-spin-button,
        .custom-number-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .custom-number-input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      <TooltipProvider delayDuration={100}>
        <div className="flex flex-col gap-2.5 w-full max-h-[80vh] overflow-y-auto pr-0.5 scrollbar-thin">
          <div className="flex flex-col gap-1 w-full">
            <span className={`text-[9px] font-bold uppercase tracking-wider mb-0.5 text-center ${
              theme === 'dark' ? 'text-zinc-500' : 'text-slate-400'
            }`}>
              {t('sidebar.shapes')}
            </span>
            <div className="grid grid-cols-6 gap-1 w-full justify-items-center">
              {drawingTools.map((tool) =>
                renderTooltipButton({
                  toolKey: tool.key,
                  label: tool.label,
                  hotkey: tool.hotkey,
                  onClick: () => setActiveTool(tool.key),
                  children: tool.icon,
                })
              )}
              
              {renderTooltipButton({
                label: t('sidebar.import_image_pdf'),
                onClick: () => fileInputRef.current?.click(),
                children: <Wand2 className="w-5 h-5" strokeWidth={1.5}/>,
              })}


              {renderTooltipButton({
                label: t('sidebar.import_svg'),
                onClick: () => svgInputRef.current?.click(),
                children: <Upload className="w-5 h-5 text-emerald-400" strokeWidth={1.5}/>,
              })}

              <input
                ref={svgInputRef}
                type="file"
                accept=".svg,image/svg+xml"
                className="hidden"
                onChange={onUploadSvg}
                data-testid="svg-file-input"
              />

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={onUploadImage}
                data-testid="image-file-input"
              />
            </div>
          </div>

          <div className={`w-full h-px ${theme === 'dark' ? 'bg-zinc-800' : 'bg-slate-100'}`}/>


          <div className="flex flex-col gap-1 w-full items-center">
            <span className={`text-[9px] font-bold uppercase tracking-wider mb-0.5 text-center ${
              theme === 'dark' ? 'text-zinc-500' : 'text-slate-400'
            }`}>
              {t('sidebar.actions')}
            </span>

            {textItem && (
              <div className={`w-full p-2 rounded-lg border space-y-3 mb-2 ${
                theme === 'dark' ? 'bg-zinc-800/50 border-zinc-700' : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between gap-2 px-1">
                  <label className={`text-[8px] uppercase font-bold whitespace-nowrap ${
                    theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'
                  }`}>{t('sidebar.text_settings.size')}</label>
                  <div className="flex items-center gap-1">
                    {renderTooltipButton({
                      label: t('sidebar.text_settings.size_decrease'),
                      onClick: () => handleTextChange('fontSize', Math.max(1, (textItem.fontSize || 30) - 1)),
                      toolKey: 'size-minus',
                      className: 'w-6 h-6 p-0',
                      children: <Minus className="w-3.5 h-3.5"/>,
                    })}
                    <input
                      type="number"
                      className={`w-10 border rounded px-0.5 py-0.5 text-[10px] focus:outline-none focus:border-indigo-500 text-center ${
                        theme === 'dark' ? 'bg-zinc-900 border-zinc-700 text-zinc-200' : 'bg-white border-slate-200 text-slate-800'
                      }`}
                      value={Math.round(textItem.fontSize || 30)}
                      onChange={(e) => handleTextChange('fontSize', parseInt(e.target.value) || 1)}
                    />
                    {renderTooltipButton({
                      label: t('sidebar.text_settings.size_increase'),
                      onClick: () => handleTextChange('fontSize', (textItem.fontSize || 30) + 1),
                      toolKey: 'size-plus',
                      className: 'w-6 h-6 p-0',
                      children: <Plus className="w-3.5 h-3.5"/>,
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 px-1">
                  <label className={`text-[8px] uppercase font-bold whitespace-nowrap ${
                    theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'
                  }`}>{t('sidebar.text_settings.font')}</label>
                  <select
                    className={`w-24 border rounded px-1 py-0.5 text-[10px] focus:outline-none focus:border-indigo-500 cursor-pointer ${
                      theme === 'dark' ? 'bg-zinc-900 border-zinc-700 text-zinc-200' : 'bg-white border-slate-200 text-slate-800'
                    }`}
                    value={textItem.data?.fontFamily || textItem.fontFamily}
                    onChange={(e) => {
                      const newFamily = e.target.value;
                      textItem.fontFamily = newFamily;
                      textItem.data = {...textItem.data, fontFamily: newFamily};
                      handleTextChange('fontFamily', newFamily);
                    }}
                  >
                    <option value="Arial">Arial</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Verdana">Verdana</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Courier New">Courier New</option>
                  </select>
                </div>

                <div className="flex items-center justify-between gap-2 px-1">
                  <label className={`text-[8px] uppercase font-bold whitespace-nowrap ${
                    theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'
                  }`}>{t('sidebar.text_settings.style')}</label>
                  <div className="flex gap-1">
                    {renderTooltipButton({
                      label: t('sidebar.text_settings.bold'),
                      onClick: () => handleTextChange('fontWeight', textItem.fontWeight === 'bold' ? 'normal' : 'bold'),
                      toolKey: 'bold',
                      className: `w-7 h-7 ${textItem.fontWeight === 'bold' ? 'bg-indigo-600 text-white shadow-indigo-500/20 shadow-sm' : ''}`,
                      children: <Bold className="w-3.5 h-3.5"/>,
                    })}
                    {renderTooltipButton({
                      label: t('sidebar.text_settings.italic'),
                      onClick: () => {
                        const isItalic = !textItem.data?.isItalic;
                        textItem.data = { ...textItem.data, isItalic };
                        textItem.shear = new paper.Point(isItalic ? 0.2 : 0, 0);
                        onProjectChange?.();
                        updateTransformUI?.();
                      },
                      toolKey: 'italic',
                      className: `w-7 h-7 ${textItem.data?.isItalic ? 'bg-indigo-600 text-white shadow-indigo-500/20 shadow-sm' : ''}`,
                      children: <Italic className="w-3.5 h-3.5"/>,
                    })}
                    {renderTooltipButton({
                      label: t('sidebar.text_settings.underline'),
                      onClick: () => {
                        textItem.data = { ...textItem.data, underline: !textItem.data?.underline };
                        onProjectChange?.();
                        updateTransformUI?.();
                      },
                      toolKey: 'underline',
                      className: `w-7 h-7 ${textItem.data?.underline ? 'bg-indigo-600 text-white shadow-indigo-500/20 shadow-sm' : ''}`,
                      children: <Underline className="w-3.5 h-3.5"/>,
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 px-1">
                  <label className={`text-[8px] uppercase font-bold whitespace-nowrap ${
                    theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'
                  }`}>{t('sidebar.text_settings.align')}</label>
                  <div className="flex gap-1">
                    {renderTooltipButton({
                      label: t('sidebar.text_settings.align_left'),
                      onClick: () => handleTextChange('justification', 'left'),
                      toolKey: 'align-left',
                      className: `w-7 h-7 ${textItem.justification === 'left' ? 'bg-indigo-600 text-white shadow-indigo-500/20 shadow-sm' : ''}`,
                      children: <AlignLeft className="w-3.5 h-3.5"/>,
                    })}
                    {renderTooltipButton({
                      label: t('sidebar.text_settings.align_center'),
                      onClick: () => handleTextChange('justification', 'center'),
                      toolKey: 'align-center',
                      className: `w-7 h-7 ${textItem.justification === 'center' ? 'bg-indigo-600 text-white shadow-indigo-500/20 shadow-sm' : ''}`,
                      children: <AlignCenter className="w-3.5 h-3.5"/>,
                    })}
                    {renderTooltipButton({
                      label: t('sidebar.text_settings.align_right'),
                      onClick: () => handleTextChange('justification', 'right'),
                      toolKey: 'align-right',
                      className: `w-7 h-7 ${textItem.justification === 'right' ? 'bg-indigo-600 text-white shadow-indigo-500/20 shadow-sm' : ''}`,
                      children: <AlignRight className="w-3.5 h-3.5"/>,
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-6 w-full justify-items-center">
              {renderTooltipButton({
                label: t('sidebar.tool_boolean_union'),
                disabled: selectedCount < 2,
                onClick: () => onBooleanOperation('unite'),
                className: 'w-7 h-7 disabled:opacity-30',
                children: <Merge className="w-3.5 h-3.5"/>,
              })}

              {renderTooltipButton({
                label: t('sidebar.tool_boolean_subtract'),
                disabled: selectedCount < 2,
                onClick: () => onBooleanOperation('subtract'),
                className: 'w-7 h-7 disabled:opacity-30',
                children: <Minus className="w-3.5 h-3.5"/>,
              })}

              {renderTooltipButton({
                label: t('sidebar.tool_group'),
                hotkey: 'Ctrl+G',
                disabled: selectedCount < 2,
                onClick: onGroup,
                className: 'w-7 h-7 disabled:opacity-30',
                children: <Group className="w-3.5 h-3.5"/>,
              })}

              {renderTooltipButton({
                label: t('sidebar.tool_ungroup'),
                hotkey: 'Ctrl+Shift+G',
                disabled: !isAnyGroupSelected,
                onClick: onUngroup,
                className: 'w-7 h-7 disabled:opacity-30',
                children: <Ungroup className="w-3.5 h-3.5"/>,
              })}

              {renderTooltipButton({
                label: t('sidebar.transform.flip_horizontal'),
                disabled: selectedCount === 0,
                onClick: () => handleFlip('horizontal'),
                className: 'w-7 h-7 p-0 disabled:opacity-30',
                children: <FlipHorizontal className="w-3.5 h-3.5"/>,
              })}

              {renderTooltipButton({
                label: t('sidebar.transform.flip_vertical'),
                disabled: selectedCount === 0,
                onClick: () => handleFlip('vertical'),
                className: 'w-7 h-7 p-0 disabled:opacity-30',
                children: <FlipVertical className="w-3.5 h-3.5"/>,
              })}
            </div>

            {selectedCount > 0 && (
              <div className={`w-full p-2 mt-2 rounded-lg border space-y-3 ${
                theme === 'dark' ? 'bg-zinc-800/50 border-zinc-700' : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex flex-col gap-3 px-1">
                  <NumberInput
                    label={t('sidebar.transform.rotation')}
                    icon={RotateCw}
                    theme={theme}
                    value={parseFloat(Number(selectedItems[0]?.data?.rotation || 0).toFixed(2))}
                    step={0.01}
                    onChange={(val, isFinal) => handleTransformChange('rotation', val, isFinal)}
                  />
                  <NumberInput
                    label={t('sidebar.transform.blur')}
                    icon={Scaling}
                    theme={theme}
                    value={parseFloat(Number(selectedItems[0]?.shadowBlur || 0).toFixed(2))}
                    step={0.01}
                    min={0}
                    onChange={(val, isFinal) => handleTransformChange('blur', val, isFinal)}
                  />
                </div>
                <div className="flex flex-col gap-3 px-1">
                  <NumberInput
                    label="X (Position)"
                    icon={Move}
                    theme={theme}
                    value={parseFloat(Number(selectedItems[0]?.bounds.x || 0).toFixed(2))}
                    step={0.01}
                    onChange={(val, isFinal) => handleTransformChange('x', val, isFinal)}
                  />
                  <NumberInput
                    label="Y (Position)"
                    icon={Move}
                    theme={theme}
                    value={parseFloat(Number(selectedItems[0]?.bounds.y || 0).toFixed(2))}
                    step={0.01}
                    onChange={(val, isFinal) => handleTransformChange('y', val, isFinal)}
                  />
                </div>
                <div className="flex flex-col gap-3 px-1">
                  <NumberInput
                    label="W (Width)"
                    icon={Maximize}
                    theme={theme}
                    value={parseFloat(Number(selectedItems[0]?.bounds.width || 0).toFixed(2))}
                    step={0.01}
                    min={1}
                    onChange={(val, isFinal) => handleTransformChange('width', val, isFinal)}
                  />
                  <NumberInput
                    label="H (Height)"
                    icon={Maximize}
                    theme={theme}
                    value={parseFloat(Number(selectedItems[0]?.bounds.height || 0).toFixed(2))}
                    step={0.01}
                    min={1}
                    onChange={(val, isFinal) => handleTransformChange('height', val, isFinal)}
                  />
                </div>

                {showRadius && (
                  <div className="flex flex-col gap-3 px-1">
                    <NumberInput
                      label={t('sidebar.transform.radius')}
                      icon={Radius}
                      theme={theme}
                      value={parseFloat(Number(selectedItems[0]?.data?.radius || 0).toFixed(2))}
                      step={0.01}
                      min={0}
                      onChange={(val, isFinal) => handleTransformChange('radius', val, isFinal)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </TooltipProvider>
    </aside>
  );
}